import { useMemo, useState } from 'react';
import { Box, Text, Flex, chakra } from '@chakra-ui/react';
import { Plus, X } from 'lucide-react';
import type { OptimizedVolunteer } from '@/utils/optimizedQueries';
import { groupByDepartment, uniqueDepartments } from '@/utils/departments';
import { departmentColorFrom, departmentColorMap, departmentTileColors } from '@/utils/departmentColors';
import {
  formatShiftTime,
  groupShiftsByDay,
  isShiftFull,
  shiftSpotsLeft,
  type EventShift,
} from '@/utils/eventShifts';

interface VolunteersShiftsViewProps {
  shifts: EventShift[];
  /** { shiftId: volunteer user ids } */
  roster: Record<string, string[]>;
  /** Everyone on the event, used for names and for the add-volunteer list. */
  volunteers: OptimizedVolunteer[];
  loading: boolean;
  onAssign: (shiftId: string, volunteerUserId: string) => Promise<void>;
  onRemove: (shiftId: string, volunteerUserId: string) => Promise<void>;
  onManageClick?: (volunteer: OptimizedVolunteer) => void;
}

function displayName(volunteer: OptimizedVolunteer | undefined): string {
  const profile = volunteer?.profiles as { first_name?: string; last_name?: string } | undefined;
  const name = profile ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() : '';
  return name || 'Volunteer';
}

/** "2 more needed" / "Full" / "4 on this shift" */
function staffingLabel(shift: EventShift): { text: string; color: string } {
  const left = shiftSpotsLeft(shift);
  if (left === null) {
    return { text: shift.taken === 1 ? '1 on this shift' : `${shift.taken} on this shift`, color: 'volyx.textSecondary' };
  }
  if (left === 0) return { text: `Full · ${shift.taken}/${shift.capacity}`, color: 'volyx.success' };
  return { text: `${left} more needed · ${shift.taken}/${shift.capacity}`, color: 'volyx.warning' };
}

/**
 * Shift coverage for organizers: who is on each shift, which shifts are short, and adding or
 * removing people without leaving the page.
 */
export function VolunteersShiftsView({
  shifts,
  roster,
  volunteers,
  loading,
  onAssign,
  onRemove,
  onManageClick,
}: VolunteersShiftsViewProps) {
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const days = useMemo(() => groupShiftsByDay(shifts), [shifts]);
  const byUserId = useMemo(() => {
    const map = new Map<string, OptimizedVolunteer>();
    for (const volunteer of volunteers) {
      if (volunteer.volunteer_id) map.set(volunteer.volunteer_id, volunteer);
    }
    return map;
  }, [volunteers]);

  // One colour per department, over every department on the event's roster -- the same set
  // the stats pie chart colours, so a department usually keeps its colour between the two.
  const colorMap = useMemo(
    () => departmentColorMap(uniqueDepartments(volunteers.flatMap((v) => v.departments ?? []))),
    [volunteers],
  );

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box mt="2" display="flex" flexDirection="column" gap="3">
        {[0, 1, 2].map((key) => (
          <Box key={key} bg="volyx.bg" borderRadius="xl" boxShadow="0 6px 18px rgba(0,0,0,0.12)" p="4">
            <Box h="4" w="40%" mb="2" bg="volyx.lightGray" borderRadius="md" />
            <Box h="3" w="25%" bg="volyx.lightGray" borderRadius="md" />
          </Box>
        ))}
      </Box>
    );
  }

  if (shifts.length === 0) {
    return (
      <Box mt="6" textAlign="center" py="10" px="6">
        <Text fontSize="sm" color="volyx.textSecondary" mb="1">
          This event has no shifts yet.
        </Text>
        <Text fontSize="xs" color="volyx.textSecondary">
          Add them when you edit the event, and volunteers can pick the ones they can work.
        </Text>
      </Box>
    );
  }

  return (
    <Box mt="2" display="flex" flexDirection="column" gap="4">
      {days.map((day) => (
        <Box key={day.dayKey}>
          <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" textTransform="uppercase" mb="2">
            {day.label}
          </Text>
          <Box display="flex" flexDirection="column" gap="3">
            {day.shifts.map((shift) => {
              const assigned = roster[shift.id] ?? [];
              const staffing = staffingLabel(shift);
              const available = volunteers.filter(
                (v) => v.volunteer_id && !assigned.includes(v.volunteer_id),
              );
              const isAdding = addingTo === shift.id;
              // Split the roster by department. Headings only appear when at least one person
              // has a department -- an event without departments stays a plain list.
              const groups = groupByDepartment(assigned, (userId) => byUserId.get(userId)?.departments ?? []);
              const showDepartments = groups.some((g) => g.label !== null);

              const renderChip = (userId: string) => {
                const volunteer = byUserId.get(userId);
                return (
                  <Flex
                    key={userId}
                    align="center"
                    gap="2"
                    pl="3"
                    pr="1.5"
                    py="1.5"
                    bg="volyx.lightGray"
                    borderRadius="full"
                  >
                    <chakra.button
                      type="button"
                      border="none"
                      bg="transparent"
                      p="0"
                      fontSize="sm"
                      color="volyx.text"
                      cursor={volunteer && onManageClick ? 'pointer' : 'default'}
                      onClick={() => volunteer && onManageClick?.(volunteer)}
                    >
                      {displayName(volunteer)}
                    </chakra.button>
                    <chakra.button
                      type="button"
                      aria-label={`Remove ${displayName(volunteer)} from this shift`}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p="1"
                      borderRadius="full"
                      border="none"
                      bg="transparent"
                      cursor="pointer"
                      _hover={{ bg: 'volyx.mediumGray' }}
                      disabled={busy}
                      onClick={() => void run(() => onRemove(shift.id, userId))}
                    >
                      <X size={14} color="var(--color-text-secondary)" />
                    </chakra.button>
                  </Flex>
                );
              };

              return (
                <Box
                  key={shift.id}
                  bg="volyx.bg"
                  borderRadius="xl"
                  boxShadow="0 6px 18px rgba(0,0,0,0.12)"
                  overflow="hidden"
                >
                  <Flex align="center" gap="3" p="4" pb="3">
                    <Box flex="1" minW="0">
                      <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" m="0">
                        {formatShiftTime(shift)}
                      </Text>
                      <Text fontSize="xs" color={staffing.color} fontWeight="medium" m="0" mt="0.5">
                        {staffing.text}
                      </Text>
                    </Box>
                    <chakra.button
                      type="button"
                      aria-label={`Add a volunteer to ${day.label} ${formatShiftTime(shift)}`}
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      gap="1"
                      px="3"
                      h="9"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="volyx.borderLight"
                      bg={isAdding ? 'volyx.lightGray' : 'transparent'}
                      color="volyx.action"
                      fontSize="xs"
                      fontWeight="semibold"
                      cursor="pointer"
                      onClick={() => setAddingTo(isAdding ? null : shift.id)}
                    >
                      <Plus size={14} />
                      Add
                    </chakra.button>
                  </Flex>

                  {assigned.length > 0 && (
                    <Box px="4" pb="3" display="flex" flexDirection="column" gap="3">
                      {groups.map((group) => {
                        // The line takes the department's colour; the name a darkened shade of
                        // it, since several palette colours are too pale to read as small text.
                        // "No department" stays neutral grey.
                        const color = group.label ? departmentColorFrom(colorMap, group.label) : null;
                        return (
                          <Box key={group.label?.toLowerCase() ?? '__no-department'}>
                            {showDepartments && (
                              <Box mb="2">
                                <Text
                                  fontSize="10px"
                                  fontWeight="semibold"
                                  color={color ? departmentTileColors(color).fg : 'volyx.textSecondary'}
                                  textTransform="uppercase"
                                  letterSpacing="wider"
                                  m="0"
                                  mb="1"
                                >
                                  {group.label ?? 'No department'}
                                </Text>
                                <Box
                                  data-department-line={group.label ?? ''}
                                  h="2px"
                                  borderRadius="full"
                                  bg={color ?? 'volyx.borderLight'}
                                />
                              </Box>
                            )}
                            <Flex flexWrap="wrap" gap="2">
                              {group.items.map(renderChip)}
                            </Flex>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {assigned.length === 0 && !isAdding && (
                    <Text fontSize="xs" color="volyx.textSecondary" px="4" pb="3" m="0">
                      Nobody on this shift yet.
                    </Text>
                  )}

                  {isAdding && (
                    <Box borderTop="1px solid" borderColor="volyx.lightGray" maxH="220px" overflowY="auto">
                      {isShiftFull(shift) && (
                        <Text fontSize="xs" color="volyx.warning" px="4" pt="3" m="0">
                          This shift is full. Adding someone needs a bigger spot count on the event.
                        </Text>
                      )}
                      {available.length === 0 ? (
                        <Text fontSize="xs" color="volyx.textSecondary" px="4" py="3" m="0">
                          Everyone on this event is already on this shift.
                        </Text>
                      ) : (
                        available.map((volunteer) => (
                          <chakra.button
                            key={volunteer.id}
                            type="button"
                            w="100%"
                            textAlign="left"
                            px="4"
                            py="2.5"
                            bg="transparent"
                            border="none"
                            fontSize="sm"
                            color="volyx.text"
                            cursor="pointer"
                            _hover={{ bg: 'volyx.lightGray' }}
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                await onAssign(shift.id, volunteer.volunteer_id as string);
                                setAddingTo(null);
                              })
                            }
                          >
                            {displayName(volunteer)}
                          </chakra.button>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
